/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import { motion, AnimatePresence } from "motion/react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn } from "@/lib/utils";

interface Tab {
  id: string;
  label: string;
  icon?: React.ReactNode;
  shortLabel?: string;
}

interface TabNavigationProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  // Desktop only -- header controls folded into the same pill as the tabs.
  actions?: React.ReactNode;
}

const SPRING_TRANSITION = {
  type: "spring" as const,
  stiffness: 500,
  damping: 30,
} as const;

export const TabNavigation: React.FC<TabNavigationProps> = ({
  tabs,
  activeTab,
  onTabChange,
  actions,
}) => {
  return (
    <>
      {/* ---- desktop: centred pill at the top ---- */}
      <div className="hidden sm:flex items-center justify-center">
        <div className="flex items-center gap-1 rounded-none bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-1.5">
          <TabsPrimitive.Root value={activeTab} onValueChange={onTabChange}>
            <TabsPrimitive.List className="flex gap-1" aria-label="Sections">
              {tabs.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <TabsPrimitive.Trigger
                    key={tab.id}
                    value={tab.id}
                    className={cn(
                      "relative px-3.5 py-2 text-[13px] font-medium tracking-[0.01em] whitespace-nowrap rounded-none",
                      "transition-colors duration-150 focus:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500/50",
                      isActive
                        ? "text-gray-900 dark:text-white"
                        : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100",
                    )}
                    title={tab.label}
                  >
                    <AnimatePresence>
                      {isActive && (
                        <motion.div
                          layoutId="activeTabDesktop"
                          className="absolute inset-0 bg-emerald-500/10 dark:bg-emerald-400/10"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={SPRING_TRANSITION}
                        />
                      )}
                    </AnimatePresence>
                    <span className="relative flex items-center gap-2">
                      <span
                        className={cn(
                          "h-1.5 w-1.5 rounded-full",
                          isActive ? "bg-emerald-500 dark:bg-emerald-400" : "bg-current opacity-40",
                        )}
                      />
                      {tab.label}
                    </span>
                  </TabsPrimitive.Trigger>
                );
              })}
            </TabsPrimitive.List>
          </TabsPrimitive.Root>

          {actions && (
            <>
              <div className="w-px self-stretch bg-gray-200 dark:bg-gray-800 mx-0.5" />
              <div className="flex items-center gap-0.5 flex-shrink-0">{actions}</div>
            </>
          )}
        </div>
      </div>

      {/* ---- mobile: floating dock ---- */}
      <nav
        className="m-dock sm:hidden flex items-stretch gap-0.5 px-2 pt-1.5"
        style={{ paddingBottom: "calc(0.375rem + env(safe-area-inset-bottom))" }}
        aria-label="Sections"
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onTabChange(tab.id)}
              aria-current={isActive ? "page" : undefined}
              className={cn(
                "relative flex min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-xl py-2",
                "transition-transform duration-150 active:scale-90 focus:outline-none",
                isActive ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400",
              )}
            >
              {isActive && (
                <motion.span
                  layoutId="mobileDockActive"
                  className="absolute left-1/2 top-0 h-[3px] w-7 -translate-x-1/2 rounded-b-full bg-emerald-600 dark:bg-emerald-500"
                  transition={SPRING_TRANSITION}
                />
              )}
              <span className={cn("relative flex-shrink-0 [&_svg]:h-[21px] [&_svg]:w-[21px]", isActive && "text-emerald-600 dark:text-emerald-400")}>{tab.icon}</span>
              <span
                className={cn(
                  "relative max-w-full truncate text-[9.5px] font-medium leading-none tracking-tight",
                  isActive ? "font-semibold opacity-100" : "opacity-80",
                )}
              >
                {tab.shortLabel || tab.label}
              </span>
            </button>
          );
        })}
      </nav>
    </>
  );
};
